import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Alert, Switch, FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { osTypesAPI } from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import { OSType } from '../../types';

interface OSTypeFormData {
  osName: string;
  osCategory: string;
  isActive: boolean;
  displayOrder: number;
}

const OSTypes = () => {
  const [osTypes, setOSTypes] = useState<OSType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOSType, setEditingOSType] = useState<OSType | null>(null);
  const [formData, setFormData] = useState<OSTypeFormData>({ osName: '', osCategory: '', isActive: true, displayOrder: 0 });
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; id: number }>({ open: false, id: 0 });

  useEffect(() => { fetchOSTypes(); }, []);

  const fetchOSTypes = async () => {
    try {
      const response = await osTypesAPI.getAll();
      setOSTypes(response.data);
      setError('');
    } catch (err) { setError('Failed to load OS types'); } finally { setLoading(false); }
  };

  const handleOpenDialog = (osType: OSType | null = null) => {
    if (osType) {
      setEditingOSType(osType);
      setFormData({ osName: osType.OSName, osCategory: osType.OSCategory || '', isActive: !!osType.IsActive, displayOrder: osType.DisplayOrder });
    } else {
      setEditingOSType(null);
      setFormData({ osName: '', osCategory: '', isActive: true, displayOrder: 0 });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => { setOpenDialog(false); setEditingOSType(null); setFormData({ osName: '', osCategory: '', isActive: true, displayOrder: 0 }); };

  const handleSubmit = async () => {
    try {
      if (editingOSType) { await osTypesAPI.update(editingOSType.OSTypeId, formData); }
      else { await osTypesAPI.create(formData); }
      fetchOSTypes(); handleCloseDialog(); setError('');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to save OS type');
    }
  };

  const handleDeleteClick = (id: number) => {
    setConfirmDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    const id = confirmDialog.id;
    setConfirmDialog({ open: false, id: 0 });
    try { await osTypesAPI.delete(id); fetchOSTypes(); setError(''); } catch (err) { setError('Failed to delete OS type'); }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">OS Types</Typography>
        <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon fontSize="small" />} onClick={() => handleOpenDialog()}>Add OS Type</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>OS Name</TableCell><TableCell>Category</TableCell><TableCell>Active</TableCell><TableCell>Display Order</TableCell><TableCell align="right">Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {osTypes.map((osType) => (
              <TableRow key={osType.OSTypeId}>
                <TableCell>{osType.OSName}</TableCell><TableCell>{osType.OSCategory || '-'}</TableCell>
                <TableCell>{osType.IsActive ? 'Yes' : 'No'}</TableCell><TableCell>{osType.DisplayOrder}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenDialog(osType)} size="small"><EditIcon fontSize="small" /></IconButton>
                  <IconButton onClick={() => handleDeleteClick(osType.OSTypeId)} size="small" color="error"><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingOSType ? 'Edit OS Type' : 'Add OS Type'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" size="small" label="OS Name" fullWidth value={formData.osName} onChange={(e) => setFormData({ ...formData, osName: e.target.value })} required />
          <TextField margin="dense" size="small" label="Category (e.g., Windows, Linux)" fullWidth value={formData.osCategory} onChange={(e) => setFormData({ ...formData, osCategory: e.target.value })} />
          <TextField margin="dense" size="small" label="Display Order" type="number" fullWidth value={formData.displayOrder} onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })} />
          <FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />} label="Active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} size="small">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="secondary" disabled={!formData.osName}>{editingOSType ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        title="Deactivate OS Type"
        message="Are you sure you want to deactivate this OS type?"
        confirmLabel="Deactivate"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ open: false, id: 0 })}
      />
    </Box>
  );
};

export default OSTypes;
